import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Users, Mail, LogOut, Inbox, TrendingUp, DollarSign, Building2, Star, Settings } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { useAuth } from "../context/AuthContext";
import { LogoLockup } from "../components/LogoMark";
import CrmLeads from "./crm/CrmLeads";
import CrmSettings from "./crm/CrmSettings";
import QuotesPanel from "./crm/QuotesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "bookings", label: "Quotes & Bookings", icon: FileText },
  { key: "leads", label: "CRM Pipeline", icon: Building2 },
  { key: "quotes", label: "Enterprise Requests", icon: Inbox },
  { key: "careers", label: "Applications", icon: Users },
  { key: "contacts", label: "Messages", icon: Mail },
  { key: "settings", label: "Settings", icon: Settings },
];
const PIE_COLORS = ["#7FA8C9", "#5E7E99", "#2F4E67", "#3a6081"];

function Counter({ value, prefix = "", suffix = "" }) {
  const [n, setN] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const target = Number(value) || 0;
    const start = performance.now();
    const dur = 900;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(target * eased);
      if (p < 1) ref.current = requestAnimationFrame(tick);
    };
    ref.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.current);
  }, [value]);
  const display = Number.isInteger(Number(value)) ? Math.round(n).toLocaleString() : n.toFixed(1);
  return <span>{prefix}{display}{suffix}</span>;
}

const TT = ({ active, payload, label }) => active && payload?.length ? (
  <div className="glass-strong rounded-xl px-3 py-2 text-xs text-white">
    <div className="text-white/60 mb-1">{label}</div>
    {payload.map((p, i) => <div key={i} style={{ color: p.color }}>{p.name}: {Number(p.value).toLocaleString()}</div>)}
  </div>
) : null;

function Overview({ token }) {
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setA(r.data))
      .catch(() => setErr(true));
  }, [token]);
  if (err) return <div className="glass rounded-3xl p-16 text-center text-white/50">Couldn't load analytics. Please refresh the page.</div>;
  if (!a) return <div className="p-16 text-center text-white/40">Loading analytics…</div>;
  const k = a.kpis;
  const kpis = [
    { icon: DollarSign, label: "Monthly Recurring", value: k.mrr, prefix: "$" },
    { icon: TrendingUp, label: "Revenue YTD", value: k.revenue_ytd, prefix: "$" },
    { icon: Building2, label: "Active Contracts", value: k.active_contracts },
    { icon: Users, label: "Clients", value: k.clients },
    { icon: Star, label: "Quality Score", value: k.quality_score, suffix: "%" },
    { icon: Star, label: "Satisfaction", value: k.satisfaction, suffix: "%" },
    { icon: DollarSign, label: "Pipeline Value", value: k.pipeline_value, prefix: "$" },
    { icon: LayoutDashboard, label: "AI Quotes", value: k.ai_quotes },
  ];
  return (
    <div className="space-y-6" data-testid="overview-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kp) => (
          <div key={kp.label} className="glass rounded-3xl p-5">
            <div className="h-9 w-9 rounded-xl bg-white/5 flex items-center justify-center mb-3"><kp.icon className="h-4 w-4 text-steel-light" /></div>
            <div className="font-serif text-2xl md:text-3xl text-white" style={{ fontWeight: 500 }}><Counter value={kp.value} prefix={kp.prefix || ""} suffix={kp.suffix || ""} /></div>
            <div className="text-white/45 text-xs mt-1">{kp.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="glass rounded-3xl p-6 lg:col-span-2">
          <p className="overline text-steel-light mb-4">Revenue Trend</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={a.revenue}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7FA8C9" stopOpacity={0.5} /><stop offset="100%" stopColor="#7FA8C9" stopOpacity={0} /></linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2F4E67" stopOpacity={0.4} /><stop offset="100%" stopColor="#2F4E67" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="month" stroke="rgba(255,255,255,0.4)" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="rgba(255,255,255,0.4)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v/1000}k`} />
              <Tooltip content={<TT />} />
              <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#7FA8C9" strokeWidth={2} fill="url(#g1)" />
              <Area type="monotone" dataKey="recurring" name="Recurring" stroke="#2F4E67" strokeWidth={2} fill="url(#g2)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="glass rounded-3xl p-6">
          <p className="overline text-steel-light mb-4">Industry Mix</p>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={a.industry_mix} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {a.industry_mix.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="none" />)}
              </Pie>
              <Tooltip content={<TT />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {a.industry_mix.map((e, i) => (
              <div key={e.name} className="flex items-center gap-2 text-xs text-white/60">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /> {e.name} · {e.value}%
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="glass rounded-3xl p-6">
        <p className="overline text-steel-light mb-4">Quote Pipeline</p>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={a.pipeline}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="stage" stroke="rgba(255,255,255,0.4)" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke="rgba(255,255,255,0.4)" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip content={<TT />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Bar dataKey="count" name="Quotes" fill="#5E7E99" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { token, logout, user } = useAuth();
  const nav = useNavigate();
  const [tab, setTabState] = useState(() => sessionStorage.getItem("np_admin_tab") || "overview");
  const setTab = (k) => { sessionStorage.setItem("np_admin_tab", k); setTabState(k); };
  const [stats, setStats] = useState({ quotes: 0, careers: 0, contacts: 0 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leadsCount, setLeadsCount] = useState(0);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/admin/stats`, auth).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/leads`, auth).then((r) => setLeadsCount(r.data.length)).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (["overview", "leads", "settings", "bookings"].includes(tab)) return;
    setLoading(true);
    axios.get(`${API}/admin/${tab}`, auth).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [tab]); // eslint-disable-line

  const doLogout = () => { logout(); nav("/admin/login"); };
  const fmt = (d) => d ? new Date(d).toLocaleString() : "—";
  const counts = { overview: "", leads: leadsCount, ...stats };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink-950 flex">
      <aside className="w-64 bg-ink-900 border-r border-white/8 text-white hidden md:flex flex-col fixed h-full">
        <div className="p-6 border-b border-white/8"><LogoLockup dark className="h-8" /></div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${tab === t.key ? "bg-white/10 text-white" : "text-white/55 hover:text-white hover:bg-white/5"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
              {counts[t.key] !== "" && counts[t.key] !== undefined && <span className="ml-auto text-xs bg-steel/30 text-steel-light rounded-full px-2 py-0.5">{counts[t.key]}</span>}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/8">
          <div className="text-white/40 text-xs mb-2 px-4 truncate">{user?.email}</div>
          <button data-testid="admin-logout" onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm text-white/55 hover:text-white hover:bg-white/5 transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 md:ml-64 min-w-0">
        <header className="glass-strong border-b border-white/8 px-6 md:px-10 py-6 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="font-serif text-2xl text-white" style={{ fontWeight: 500 }}>{TABS.find((t) => t.key === tab)?.label}</h1>
            <p className="text-white/45 text-sm">North Peak executive control center.</p>
          </div>
          <button onClick={doLogout} className="md:hidden text-white"><LogOut className="h-5 w-5" /></button>
        </header>

        <div className="p-6 md:p-10">
          <div className="flex md:hidden gap-2 mb-4 overflow-x-auto">
            {TABS.map((t) => (
              <button key={t.key} data-testid={`mtab-${t.key}`} onClick={() => setTab(t.key)}
                className={`btn-pill px-4 py-2 text-xs whitespace-nowrap ${tab === t.key ? "bg-white text-ink-950" : "glass text-white/60"}`}>{t.label}</button>
            ))}
          </div>

          {tab === "overview" ? <Overview token={token} />
            : tab === "bookings" ? <QuotesPanel token={token} />
            : tab === "leads" ? <CrmLeads token={token} />
            : tab === "settings" ? <CrmSettings token={token} />
            : (
            <div className="glass rounded-3xl overflow-hidden">
              {loading ? (
                <div className="p-16 text-center text-white/40">Loading…</div>
              ) : rows.length === 0 ? (
                <div className="p-16 text-center text-white/40" data-testid="empty-state"><Inbox className="h-10 w-10 mx-auto mb-4 opacity-40" />No submissions yet.</div>
              ) : (
                <div className="divide-y divide-white/8" data-testid={`list-${tab}`}>
                  {rows.map((r) => (
                    <div key={r.id} className="p-6 hover:bg-white/[0.02] transition-colors">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="font-600 text-white">{r.company_name || r.name} {r.company && <span className="text-white/40 font-400">· {r.company}</span>}</div>
                          <div className="text-white/50 text-sm mt-0.5">{r.email}{r.phone && ` · ${r.phone}`}</div>
                        </div>
                        <div className="text-white/35 text-xs">{fmt(r.created_at)}</div>
                      </div>
                      <div className="mt-3 text-sm text-white/65 space-y-1">
                        {tab === "quotes" && <>
                          <div><span className="text-white/40">Facility:</span> {r.facility_type} {r.square_footage && `· ${r.square_footage}`}</div>
                          {r.services?.length > 0 && <div><span className="text-white/40">Services:</span> {r.services.join(", ")}</div>}
                          {r.frequency && <div><span className="text-white/40">Frequency:</span> {r.frequency}</div>}
                          {r.message && <div className="text-white/55 italic mt-1">"{r.message}"</div>}
                        </>}
                        {tab === "careers" && <>
                          <div><span className="text-white/40">Position:</span> {r.position} {r.location && `· ${r.location}`}</div>
                          {r.experience && <div><span className="text-white/40">Experience:</span> {r.experience}</div>}
                          {r.cover_letter && <div className="text-white/55 italic mt-1">"{r.cover_letter}"</div>}
                        </>}
                        {tab === "contacts" && <>
                          {r.company && <div><span className="text-white/40">Company:</span> {r.company}</div>}
                          {(r.property_type || r.service_requested) && <div><span className="text-white/40">Type / Service:</span> {[r.property_type, r.service_requested].filter(Boolean).join(" · ")}</div>}
                          {r.property_address && <div><span className="text-white/40">Address:</span> {r.property_address}</div>}
                          {r.subject && <div><span className="text-white/40">Subject:</span> {r.subject}</div>}
                          <div className="text-white/55 italic mt-1">"{r.message}"</div>
                        </>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
